import React, { useEffect } from "react";
import {
  Box,
  Button,
  VStack,
  Input,
  Text,
  HStack,
} from "@chakra-ui/react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Drawer, {
  DrawerHeader,
  DrawerTitle,
  DrawerBody,
  DrawerFooter,
  DrawerCloseTrigger,
} from "@/components/ui/Drawer";
import {
  FormErrorDisplay,
  RootErrorDisplay,
} from "@/components/ui/FormErrorDisplay";
import Select from "@/components/ui/Select";
import withHashDrawer from "@/components/ui/withHashDrawer";
import {
  useCreateQrCodeMutation,
  useUpdateQrCodeMutation,
  useGetQrCodeQuery,
} from "../api";
import { useFormErrorHandler } from "@/hooks/useFormErrorHandler";

// Schema definition based on swagger API
const qrCodeSchema = yup.object({
  key: yup
    .string()
    .required("Key is required")
    .min(1, "Key cannot be empty")
    .max(255, "Key must be less than 255 characters"),
  full_name: yup
    .string()
    .nullable()
    .optional()
    .max(255, "Name must be less than 255 characters"),
  destination_link: yup
    .string()
    .required("Destination URL is required")
    .url("Must be a valid URL")
    .max(2000, "URL must be less than 2000 characters"),
  opened: yup
    .boolean()
    .nullable()
    .optional(),
});

type QrCodeFormData = {
  key: string;
  full_name?: string | null;
  destination_link: string;
  opened?: boolean | null;
};

interface QrCodeCreateEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number; // ID of QR code to fetch (for hash modal edit mode)
  onSuccess?: () => void;
}

const QrCodeCreateEditModal: React.FC<QrCodeCreateEditModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  // Fetch individual entity if entityId is provided (hash modal edit mode)
  const { data: editingQrCode } = useGetQrCodeQuery(entityId!, {
    skip: !entityId,
  });

  const isEditing = Boolean(entityId && editingQrCode);


  const {
    handleSubmit,
    reset,
    control,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(qrCodeSchema),
    defaultValues: {
      key: "",
      full_name: "",
      destination_link: "",
      opened: null,
    },
  });

  const { withErrorHandling, clearAllErrors } = useFormErrorHandler(
    setError,
    clearErrors
  );

  const [createQrCode, { isLoading: isCreating }] = useCreateQrCodeMutation();
  const [updateQrCode, { isLoading: isUpdating }] = useUpdateQrCodeMutation();

  const isLoading = isCreating || isUpdating || isSubmitting;

  // Handle modal close with form reset
  const handleClose = () => {
    reset({
      key: "",
      full_name: "",
      destination_link: "",
      opened: null,
    });
    clearAllErrors();
    onClose();
  };

  // Reset form when modal opens or editingQrCode changes
  useEffect(() => {
    if (isOpen && editingQrCode) {
      // Now we have the full response structure with all fields
      reset({
        key: editingQrCode.key || "",
        full_name: editingQrCode.full_name || "",
        destination_link: editingQrCode.destination_link || "",
        opened: editingQrCode.opened === 1 ? true : editingQrCode.opened === 0 ? false : null,
      });
    } else if (isOpen && !entityId) {
      // New mode - reset to empty values
      reset({
        key: "",
        full_name: "",
        destination_link: "",
        opened: null,
      });
    }
    
    if (isOpen) {
      // Clear any previous server errors
      clearAllErrors();
    }
  }, [isOpen, editingQrCode, entityId, reset, clearAllErrors]);

  const onSubmit = async (data: any) => {
    await withErrorHandling(async () => {
      const payload = {
        key: data.key,
        destination_link: data.destination_link,
        ...(data.full_name?.trim() && { full_name: data.full_name.trim() }),
        ...(data.opened !== null && { opened: data.opened }),
      };

      if (editingQrCode) {
        await updateQrCode({
          id: editingQrCode.id,
          data: payload,
        }).unwrap();
      } else {
        await createQrCode(payload).unwrap();
      }

      onSuccess?.();
      handleClose();
    });
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="md">
      <DrawerHeader>
        <DrawerTitle color="brand.700" _dark={{ color: "brand.300" }}>
          {editingQrCode ? "Edit QR Code Pixel Share" : "Create New QR Code Pixel Share"}
        </DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>

      <DrawerBody>
        <form onSubmit={handleSubmit(onSubmit)} id="qr-code-form">
          <VStack gap={4} align="stretch">
            <RootErrorDisplay />

            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                Key
              </Text>
              <Controller
                name="key"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Enter unique key"
                    bg="white"
                    _dark={{ bg: "gray.700" }}
                    borderColor={errors.key ? "red.500" : undefined}
                    disabled={isLoading}
                  />
                )}
              />
              <FormErrorDisplay error={errors.key} />
            </Box>

            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                Name
              </Text>
              <Controller
                name="full_name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    placeholder="Enter name"
                    bg="white"
                    _dark={{ bg: "gray.700" }}
                    borderColor={errors.full_name ? "red.500" : undefined}
                    disabled={isLoading}
                  />
                )}
              />
              <FormErrorDisplay error={errors.full_name} />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Optional - Display name for the QR code
              </Text>
            </Box>

            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                Destination URL
              </Text>
              <Controller
                name="destination_link"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="https://example.com"
                    bg="white"
                    _dark={{ bg: "gray.700" }}
                    borderColor={errors.destination_link ? "red.500" : undefined}
                    disabled={isLoading}
                    type="url"
                  />
                )}
              />
              <FormErrorDisplay error={errors.destination_link} />
            </Box>

            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                Status
              </Text>
              <Controller
                name="opened"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <Select
                    {...field}
                    value={value === null ? "" : value ? "true" : "false"}
                    onChange={(val) => onChange(val === "" ? null : val === "true")}
                    placeholder="Select status"
                    disabled={isLoading}
                    options={[
                      { value: "", label: "Not Set" },
                      { value: "true", label: "Opened" },
                      { value: "false", label: "Not Opened" },
                    ]}
                  />
                )}
              />
              <FormErrorDisplay error={errors.opened} />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Optional - Whether the QR code has been opened
              </Text>
            </Box>
          </VStack>
        </form>
      </DrawerBody>

      <DrawerFooter>
        <HStack gap={3}>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="qr-code-form"
            colorPalette="brand"
            loading={isLoading}
          >
            {editingQrCode ? "Update QR Code Pixel Share" : "Create QR Code Pixel Share"}
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

// Export wrapped versions for create and edit modes
export const QrCodeCreateModal = withHashDrawer(QrCodeCreateEditModal, { 
  action: 'new'  // Create modal: /#/new
});

export const QrCodeEditModal = withHashDrawer(QrCodeCreateEditModal, { 
  action: 'edit' 
});

// Default export for backward compatibility - we'll use create mode
export default QrCodeCreateModal; 